import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Table, TableBody,
  TableCell, TableHead, TableRow, TableContainer, Divider, Alert,
  LinearProgress, Tabs, Tab, Switch, FormControlLabel, TextField,
  Select, MenuItem, InputLabel, FormControl, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip, IconButton, Badge,
  Stepper, Step, StepLabel, Card, CardContent, CardHeader,
} from '@mui/material';
import {
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as ReferredIcon,
  Gavel as RulesIcon,
  Psychology as AIIcon,
  VerifiedUser as STPIcon,
  AccountTree as WorkflowIcon,
  Timeline as TimelineIcon,
  Info as InfoIcon,
  PlayArrow as RunIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Speed as SpeedIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';

interface DecisionRule {
  id: string;
  name: string;
  type: 'threshold' | 'conditional' | 'exclusion';
  field: string;
  operator: string;
  value: string | number;
  action: 'approve' | 'decline' | 'refer';
  priority: number;
  active: boolean;
}

interface DecisionOutcome {
  applicantId: string;
  applicantName: string;
  creditScore: number;
  pd: number;
  riskBand: string;
  decision: 'Approved' | 'Declined' | 'Referred';
  creditLimit: number;
  tenure: number;
  interestRate: number;
  declineReasons: string[];
  nextBestAction: string;
  aiConfidence: number;
  stpEligible: boolean;
  timestamp: string;
}

const mockDecisions: DecisionOutcome[] = [
  {
    applicantId: 'APP-001',
    applicantName: 'Tendai Moyo',
    creditScore: 742,
    pd: 2.1,
    riskBand: 'Low',
    decision: 'Approved',
    creditLimit: 50000,
    tenure: 24,
    interestRate: 18.5,
    declineReasons: [],
    nextBestAction: 'Proceed to disbursement',
    aiConfidence: 94,
    stpEligible: true,
    timestamp: '2026-03-01 09:15',
  },
  {
    applicantId: 'APP-002',
    applicantName: 'Chipo Enterprises Ltd',
    creditScore: 581,
    pd: 12.4,
    riskBand: 'Medium',
    decision: 'Referred',
    creditLimit: 0,
    tenure: 0,
    interestRate: 0,
    declineReasons: [],
    nextBestAction: 'Manual review required — borderline risk profile',
    aiConfidence: 67,
    stpEligible: false,
    timestamp: '2026-03-01 10:02',
  },
  {
    applicantId: 'APP-003',
    applicantName: 'Rudo Ncube',
    creditScore: 388,
    pd: 34.7,
    riskBand: 'High',
    decision: 'Declined',
    creditLimit: 0,
    tenure: 0,
    interestRate: 0,
    declineReasons: ['Credit score below minimum threshold (400)', 'High probability of default (>25%)', 'Insufficient repayment history'],
    nextBestAction: 'Eligible to re-apply after 90 days with improved credit history',
    aiConfidence: 89,
    stpEligible: false,
    timestamp: '2026-03-01 10:45',
  },
  {
    applicantId: 'APP-004',
    applicantName: 'Farai Agro SME',
    creditScore: 668,
    pd: 7.8,
    riskBand: 'Medium',
    decision: 'Approved',
    creditLimit: 120000,
    tenure: 36,
    interestRate: 22.0,
    declineReasons: [],
    nextBestAction: 'Seasonal adjustment applied — harvest-linked repayment schedule',
    aiConfidence: 81,
    stpEligible: false,
    timestamp: '2026-03-01 11:30',
  },
];

const mockRules: DecisionRule[] = [
  { id: 'R001', name: 'Minimum Credit Score', type: 'threshold', field: 'creditScore', operator: '>=', value: 400, action: 'decline', priority: 1, active: true },
  { id: 'R002', name: 'Max Probability of Default', type: 'threshold', field: 'pd', operator: '<=', value: 25, action: 'decline', priority: 2, active: true },
  { id: 'R003', name: 'Fraud Flag Exclusion', type: 'exclusion', field: 'fraudFlag', operator: '==', value: 'true', action: 'decline', priority: 3, active: true },
  { id: 'R004', name: 'High Score STP Approval', type: 'threshold', field: 'creditScore', operator: '>=', value: 700, action: 'approve', priority: 4, active: true },
  { id: 'R005', name: 'Borderline Referral', type: 'conditional', field: 'creditScore', operator: 'between', value: '400-600', action: 'refer', priority: 5, active: true },
  { id: 'R006', name: 'Blacklisted Device', type: 'exclusion', field: 'deviceStatus', operator: '==', value: 'blacklisted', action: 'decline', priority: 6, active: true },
  { id: 'R007', name: 'SME Cash-Flow Conditional', type: 'conditional', field: 'cashFlowStability', operator: '>=', value: 70, action: 'approve', priority: 7, active: false },
];

const stpConfig = [
  { segment: 'Salaried Individual', product: 'Personal Loan', minScore: 700, maxPD: 8, stpEnabled: true },
  { segment: 'SME', product: 'Working Capital', minScore: 650, maxPD: 12, stpEnabled: false },
  { segment: 'Gig Worker', product: 'Micro Loan', minScore: 600, maxPD: 15, stpEnabled: true },
  { segment: 'Agricultural SME', product: 'Farm Input Loan', minScore: 580, maxPD: 18, stpEnabled: false },
];

const getDecisionColor = (d: string) => {
  if (d === 'Approved') return 'success';
  if (d === 'Declined') return 'error';
  return 'warning';
};

const getDecisionIcon = (d: string) => {
  if (d === 'Approved') return <ApprovedIcon fontSize="small" />;
  if (d === 'Declined') return <RejectedIcon fontSize="small" />;
  return <ReferredIcon fontSize="small" />;
};

const getRiskColor = (band: string) => {
  if (band === 'Low') return '#16a34a';
  if (band === 'Medium') return '#d97706';
  return '#dc2626';
};

export default function CreditDecisioningEngine() {
  const [tab, setTab] = useState(0);
  const [selectedDecision, setSelectedDecision] = useState<DecisionOutcome | null>(null);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const [simScore, setSimScore] = useState(650);
  const [simPD, setSimPD] = useState(10);
  const [simResult, setSimResult] = useState<string | null>(null);

  const handleSimulate = () => {
    let result = 'Approved';
    if (simScore < 400 || simPD > 25) result = 'Declined';
    else if (simScore < 600) result = 'Referred';
    setSimResult(result);
  };

  const approvedCount = mockDecisions.filter(d => d.decision === 'Approved').length;
  const declinedCount = mockDecisions.filter(d => d.decision === 'Declined').length;
  const referredCount = mockDecisions.filter(d => d.decision === 'Referred').length;
  const stpCount = mockDecisions.filter(d => d.stpEligible).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} color="primary" gutterBottom>
          Credit Decisioning Engine
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Rules + AI Orchestration · Configurable Policies · Straight-Through Processing · Maker-Checker Workflows
        </Typography>
      </Box>

      {/* KPI Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Approved', value: approvedCount, color: '#16a34a', bg: '#f0fdf4', icon: <ApprovedIcon />, pct: Math.round((approvedCount / mockDecisions.length) * 100) },
          { label: 'Declined', value: declinedCount, color: '#dc2626', bg: '#fef2f2', icon: <RejectedIcon />, pct: Math.round((declinedCount / mockDecisions.length) * 100) },
          { label: 'Referred', value: referredCount, color: '#d97706', bg: '#fffbeb', icon: <ReferredIcon />, pct: Math.round((referredCount / mockDecisions.length) * 100) },
          { label: 'STP Processed', value: stpCount, color: '#1565c0', bg: '#eff6ff', icon: <STPIcon />, pct: Math.round((stpCount / mockDecisions.length) * 100) },
        ].map(kpi => (
          <Grid size={{ xs: 6, md: 3 }} key={kpi.label}>
            <Paper sx={{ p: 2, bgcolor: kpi.bg, border: `1px solid ${kpi.color}22`, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: kpi.color }}>{kpi.icon}</Box>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>{kpi.label}</Typography>
              </Box>
              <Typography variant="h4" fontWeight={800} sx={{ color: kpi.color }}>{kpi.value}</Typography>
              <LinearProgress
                variant="determinate"
                value={kpi.pct}
                sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: `${kpi.color}22`, '& .MuiLinearProgress-bar': { bgcolor: kpi.color } }}
              />
              <Typography variant="caption" color="text.secondary">{kpi.pct}% of total decisions</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<TimelineIcon />} iconPosition="start" label="Decision Log" />
          <Tab icon={<RulesIcon />} iconPosition="start" label="Business Rules" />
          <Tab icon={<AIIcon />} iconPosition="start" label="AI Orchestration" />
          <Tab icon={<STPIcon />} iconPosition="start" label="STP Configuration" />
          <Tab icon={<WorkflowIcon />} iconPosition="start" label="Maker-Checker" />
          <Tab icon={<SpeedIcon />} iconPosition="start" label="Rule Simulator" />
        </Tabs>

        <Box sx={{ p: 3 }}>

          {/* Tab 0: Decision Log */}
          {tab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Recent Decision Log</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<PolicyIcon />}>Export Decisions</Button>
                </Box>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell><strong>Applicant</strong></TableCell>
                      <TableCell><strong>Score</strong></TableCell>
                      <TableCell><strong>PD %</strong></TableCell>
                      <TableCell><strong>Risk Band</strong></TableCell>
                      <TableCell><strong>AI Confidence</strong></TableCell>
                      <TableCell><strong>Decision</strong></TableCell>
                      <TableCell><strong>Credit Limit</strong></TableCell>
                      <TableCell><strong>STP</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockDecisions.map(d => (
                      <TableRow key={d.applicantId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{d.applicantName}</Typography>
                          <Typography variant="caption" color="text.secondary">{d.applicantId} · {d.timestamp}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={700} color={d.creditScore >= 700 ? '#16a34a' : d.creditScore >= 500 ? '#d97706' : '#dc2626'}>
                            {d.creditScore}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography color={d.pd <= 10 ? '#16a34a' : d.pd <= 25 ? '#d97706' : '#dc2626'} fontWeight={600}>
                            {d.pd}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={d.riskBand}
                            size="small"
                            sx={{ bgcolor: `${getRiskColor(d.riskBand)}22`, color: getRiskColor(d.riskBand), fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={d.aiConfidence}
                              sx={{ width: 60, height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: d.aiConfidence >= 80 ? '#16a34a' : '#d97706' } }}
                            />
                            <Typography variant="caption">{d.aiConfidence}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getDecisionIcon(d.decision)}
                            label={d.decision}
                            color={getDecisionColor(d.decision) as any}
                            size="small"
                            variant="filled"
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell>
                          {d.creditLimit > 0
                            ? <Typography fontWeight={600} color="#1565c0">ZiG {d.creditLimit.toLocaleString('en-ZW')}</Typography>
                            : <Typography color="text.secondary">—</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          {d.stpEligible
                            ? <Chip label="Auto" color="success" size="small" />
                            : <Chip label="Manual" color="default" size="small" />
                          }
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => setSelectedDecision(d)}>Details</Button>
                          {d.decision === 'Declined' && (
                            <Button size="small" color="warning" onClick={() => { setSelectedDecision(d); setOverrideDialogOpen(true); }}>
                              Override
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Decision Detail Panel */}
              {selectedDecision && !overrideDialogOpen && (
                <Box sx={{ mt: 3, p: 3, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Decision Explainability — {selectedDecision.applicantName}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom color="primary">Inputs Used</Typography>
                        {[
                          { label: 'Credit Score', value: selectedDecision.creditScore },
                          { label: 'Probability of Default', value: `${selectedDecision.pd}%` },
                          { label: 'Risk Band', value: selectedDecision.riskBand },
                          { label: 'AI Confidence', value: `${selectedDecision.aiConfidence}%` },
                        ].map(row => (
                          <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px dashed #e2e8f0' }}>
                            <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                            <Typography variant="body2" fontWeight={600}>{row.value}</Typography>
                          </Box>
                        ))}
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom color="primary">Decision Outcome</Typography>
                        <Chip
                          icon={getDecisionIcon(selectedDecision.decision)}
                          label={selectedDecision.decision}
                          color={getDecisionColor(selectedDecision.decision) as any}
                          sx={{ mb: 1, fontWeight: 700 }}
                        />
                        {selectedDecision.creditLimit > 0 && (
                          <>
                            <Typography variant="body2"><strong>Credit Limit:</strong> ZiG {selectedDecision.creditLimit.toLocaleString('en-ZW')}</Typography>
                            <Typography variant="body2"><strong>Tenure:</strong> {selectedDecision.tenure} months</Typography>
                            <Typography variant="body2"><strong>Interest Rate:</strong> {selectedDecision.interestRate}% p.a.</Typography>
                          </>
                        )}
                        {selectedDecision.declineReasons.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" fontWeight={600} color="error">Decline Reasons:</Typography>
                            {selectedDecision.declineReasons.map((r, i) => (
                              <Typography key={i} variant="body2" color="error.main">• {r}</Typography>
                            ))}
                          </Box>
                        )}
                        <Alert severity="info" sx={{ mt: 1 }} icon={false}>
                          <Typography variant="caption"><strong>Next Best Action:</strong> {selectedDecision.nextBestAction}</Typography>
                        </Alert>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {/* Tab 1: Business Rules */}
          {tab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Configurable Business Rules</Typography>
                <Button variant="contained" startIcon={<AddIcon />} size="small">Add Rule</Button>
              </Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Rules are evaluated in priority order. Exclusion rules (fraud, blacklist) always take precedence. Changes are versioned and can be tested via the Rule Simulator tab.
              </Alert>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell><strong>Priority</strong></TableCell>
                      <TableCell><strong>Rule Name</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Condition</strong></TableCell>
                      <TableCell><strong>Action</strong></TableCell>
                      <TableCell><strong>Active</strong></TableCell>
                      <TableCell><strong>Edit</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockRules.map(rule => (
                      <TableRow key={rule.id} hover sx={{ opacity: rule.active ? 1 : 0.5 }}>
                        <TableCell><Chip label={`P${rule.priority}`} size="small" /></TableCell>
                        <TableCell><Typography fontWeight={600}>{rule.name}</Typography></TableCell>
                        <TableCell>
                          <Chip
                            label={rule.type}
                            size="small"
                            color={rule.type === 'exclusion' ? 'error' : rule.type === 'threshold' ? 'primary' : 'warning'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f1f5f9', px: 1, py: 0.5, borderRadius: 1 }}>
                            {rule.field} {rule.operator} {rule.value}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={rule.action.toUpperCase()}
                            size="small"
                            color={rule.action === 'approve' ? 'success' : rule.action === 'decline' ? 'error' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch checked={rule.active} size="small" />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 2: AI Orchestration */}
          {tab === 2 && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>AI-Augmented Decisioning</Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  AI recommendations are advisory by default. Straight-Through Processing (STP) must be explicitly enabled per segment/product to allow fully automated approvals.
                </Alert>
              </Grid>
              {[
                { title: 'AI Decision Recommendations', desc: 'Risk segmentation via clustering models. AI score fed into decisioning layer alongside rule outputs.', status: 'Active', color: '#16a34a' },
                { title: 'Early Warning Flags', desc: 'High-risk approvals flagged for enhanced monitoring. Anomaly detection on behavioral patterns.', status: 'Active', color: '#16a34a' },
                { title: 'Confidence-Based Escalation', desc: 'Decisions with AI confidence < 70% are automatically escalated to manual review queue.', status: 'Active', color: '#16a34a' },
                { title: 'Dynamic Policy Adjustment', desc: 'Portfolio performance feedback loop adjusts risk thresholds quarterly based on default rates.', status: 'Beta', color: '#d97706' },
                { title: 'Drift Detection', desc: 'Monitors model feature distributions for data drift. Alerts raised when drift exceeds 15%.', status: 'Active', color: '#16a34a' },
                { title: 'Continuous Retraining', desc: 'Models retrained monthly using updated repayment data. Version-controlled with rollback capability.', status: 'Scheduled', color: '#1565c0' },
              ].map(item => (
                <Grid size={{ xs: 12, md: 6 }} key={item.title}>
                  <Paper sx={{ p: 2.5, border: `1px solid ${item.color}33`, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography fontWeight={700}>{item.title}</Typography>
                      <Chip label={item.status} size="small" sx={{ bgcolor: `${item.color}22`, color: item.color, fontWeight: 700 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                  </Paper>
                </Grid>
              ))}

              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Score-to-Decision Mapping</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                        <TableRow>
                          <TableCell><strong>Score Range</strong></TableCell>
                          <TableCell><strong>Risk Band</strong></TableCell>
                          <TableCell><strong>PD Range</strong></TableCell>
                          <TableCell><strong>Default Decision</strong></TableCell>
                          <TableCell><strong>Max Credit Limit (ZiG)</strong></TableCell>
                          <TableCell><strong>Rate Band</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[
                          { range: '750 – 850', band: 'Very Low', pd: '< 3%', decision: 'Auto Approve (STP)', limit: '200,000', rate: '15 – 18%' },
                          { range: '650 – 749', band: 'Low', pd: '3 – 8%', decision: 'Approve', limit: '100,000', rate: '18 – 22%' },
                          { range: '500 – 649', band: 'Medium', pd: '8 – 20%', decision: 'Refer / Manual', limit: '50,000', rate: '22 – 28%' },
                          { range: '400 – 499', band: 'High', pd: '20 – 35%', decision: 'Refer / Enhanced Review', limit: '15,000', rate: '28 – 35%' },
                          { range: '< 400', band: 'Very High', pd: '> 35%', decision: 'Decline', limit: '—', rate: '—' },
                        ].map(row => (
                          <TableRow key={row.range} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{row.range}</TableCell>
                            <TableCell><Chip label={row.band} size="small" sx={{ bgcolor: row.band.includes('Very Low') || row.band === 'Low' ? '#f0fdf4' : row.band === 'Medium' ? '#fffbeb' : '#fef2f2', color: row.band.includes('Low') ? '#16a34a' : row.band === 'Medium' ? '#d97706' : '#dc2626', fontWeight: 700 }} /></TableCell>
                            <TableCell>{row.pd}</TableCell>
                            <TableCell>{row.decision}</TableCell>
                            <TableCell>{row.limit}</TableCell>
                            <TableCell>{row.rate}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tab 3: STP Configuration */}
          {tab === 3 && (
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>Straight-Through Processing (STP) Configuration</Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                STP enables fully automated approvals. Configure thresholds carefully — data gaps or anomaly flags trigger automatic fallback to manual review.
              </Alert>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell><strong>Segment</strong></TableCell>
                      <TableCell><strong>Product</strong></TableCell>
                      <TableCell><strong>Min Score for STP</strong></TableCell>
                      <TableCell><strong>Max PD for STP</strong></TableCell>
                      <TableCell><strong>STP Enabled</strong></TableCell>
                      <TableCell><strong>Edit</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stpConfig.map((s, i) => (
                      <TableRow key={i} hover>
                        <TableCell><Typography fontWeight={600}>{s.segment}</Typography></TableCell>
                        <TableCell>{s.product}</TableCell>
                        <TableCell>
                          <Chip label={s.minScore} color="primary" size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip label={`${s.maxPD}%`} color="secondary" size="small" />
                        </TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={<Switch defaultChecked={s.stpEnabled} color="success" />}
                            label={s.stpEnabled ? 'Enabled' : 'Disabled'}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Fallback triggers:</strong> Missing data fields, fraud flag raised, AI confidence &lt; 60%, or loan amount exceeding segment threshold will automatically route to manual review regardless of STP configuration.
              </Alert>
            </Box>
          )}

          {/* Tab 4: Maker-Checker */}
          {tab === 4 && (
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>Maker-Checker Workflow & Override Management</Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2.5, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom color="primary">Override Queue</Typography>
                    {[
                      { id: 'OVR-001', applicant: 'Tendai Moyo', originalDecision: 'Declined', requestedBy: 'loan.officer@finscore.ai', reason: 'Customer has co-guarantor', status: 'Pending Approval' },
                      { id: 'OVR-002', applicant: 'Mutsa Holdings Ltd', originalDecision: 'Referred', requestedBy: 'underwriter@finscore.ai', reason: 'Sector-specific risk adjustment applied', status: 'Approved' },
                    ].map(o => (
                      <Box key={o.id} sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography fontWeight={700}>{o.applicant}</Typography>
                          <Chip label={o.status} size="small" color={o.status === 'Approved' ? 'success' : 'warning'} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">Original: <strong>{o.originalDecision}</strong> · Requested by: {o.requestedBy}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}><em>Justification: {o.reason}</em></Typography>
                        {o.status === 'Pending Approval' && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                            <Button size="small" variant="contained" color="success">Approve Override</Button>
                            <Button size="small" variant="outlined" color="error">Reject Override</Button>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2.5, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom color="primary">Approval Hierarchy</Typography>
                    <Stepper orientation="vertical" activeStep={-1}>
                      {[
                        { label: 'Loan Officer', desc: 'Can initiate override requests with mandatory justification' },
                        { label: 'Senior Underwriter', desc: 'Reviews overrides for amounts up to ZiG 100,000' },
                        { label: 'Credit Risk Manager', desc: 'Final approval for high-value overrides > ZiG 100,000' },
                        { label: 'Audit Trail', desc: 'All overrides recorded with timestamp, user, and reason' },
                      ].map(s => (
                        <Step key={s.label} expanded>
                          <StepLabel><Typography fontWeight={600}>{s.label}</Typography></StepLabel>
                          <Box sx={{ ml: 3.5, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">{s.desc}</Typography>
                          </Box>
                        </Step>
                      ))}
                    </Stepper>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab 5: Rule Simulator */}
          {tab === 5 && (
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>Rule & Policy Simulator</Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Test how rule changes affect decision outcomes before deploying to production. Changes are isolated in simulation mode and do not affect live decisions.
              </Alert>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Input Parameters</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" gutterBottom>Credit Score: <strong>{simScore}</strong></Typography>
                        <input type="range" min={300} max={850} value={simScore} onChange={e => setSimScore(Number(e.target.value))} style={{ width: '100%' }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" gutterBottom>Probability of Default: <strong>{simPD}%</strong></Typography>
                        <input type="range" min={0} max={60} value={simPD} onChange={e => setSimPD(Number(e.target.value))} style={{ width: '100%' }} />
                      </Box>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Customer Segment</InputLabel>
                        <Select defaultValue="individual" label="Customer Segment">
                          <MenuItem value="individual">Individual — Salaried</MenuItem>
                          <MenuItem value="gig">Individual — Gig Worker</MenuItem>
                          <MenuItem value="sme">SME — Small Trader</MenuItem>
                          <MenuItem value="agri">SME — Agricultural</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Fraud Flag</InputLabel>
                        <Select defaultValue="none" label="Fraud Flag">
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="flagged">Flagged</MenuItem>
                          <MenuItem value="blacklisted">Device Blacklisted</MenuItem>
                        </Select>
                      </FormControl>
                      <Button variant="contained" startIcon={<RunIcon />} onClick={handleSimulate} size="large">
                        Run Simulation
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 7 }}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Simulation Result</Typography>
                    {simResult ? (
                      <Box>
                        <Alert
                          severity={simResult === 'Approved' ? 'success' : simResult === 'Declined' ? 'error' : 'warning'}
                          sx={{ mb: 2, fontSize: '1rem' }}
                        >
                          <strong>Decision: {simResult}</strong>
                        </Alert>
                        <Typography variant="body2" gutterBottom><strong>Applied Rules:</strong></Typography>
                        {mockRules.filter(r => r.active).map(r => (
                          <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip
                              label={simResult === r.action.replace('approve', 'Approved').replace('decline', 'Declined').replace('refer', 'Referred') ? '✓ Matched' : '✗ Skipped'}
                              size="small"
                              color={r.action === 'approve' ? 'success' : r.action === 'decline' ? 'error' : 'warning'}
                              variant="outlined"
                            />
                            <Typography variant="body2">{r.name}: {r.field} {r.operator} {r.value}</Typography>
                          </Box>
                        ))}
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          This is a simulation only. Deploy rule changes via the Business Rules tab to affect live decisions.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        <SpeedIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                        <Typography>Adjust parameters and click Run Simulation</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Override Dialog */}
      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Decision Override</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Override requests require mandatory justification and are subject to maker-checker approval before taking effect.
          </Alert>
          {selectedDecision && (
            <Box sx={{ mb: 2 }}>
              <Typography><strong>Applicant:</strong> {selectedDecision.applicantName}</Typography>
              <Typography><strong>Original Decision:</strong> {selectedDecision.decision}</Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Justification (mandatory)"
            value={overrideReason}
            onChange={e => setOverrideReason(e.target.value)}
            placeholder="Provide clear business justification for this override..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={!overrideReason.trim()} onClick={() => setOverrideDialogOpen(false)}>
            Submit for Approval
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
